import {
  fetchShortCode as fetchShortCodeApi,
  fetchShortCodeStatus as fetchShortCodeStatusApi,
} from 'support/api/VideoApi';
import {
  fetchAccessToken as fetchParamountPlusTokenApi,
  revokeToken as revokeParamountPlusTokenApi,
  fetchUserInfo as fetchParamountPlusUserInfoApi,
} from 'support/api/ParamountPlusApi';
import {
  logout as logoutApi,
  fetchAuthN as fetchAuthNApi,
  fetchAuthZ as fetchAuthZApi,
  fetchMediaToken as fetchMediaTokenApi,
} from 'support/api/AdobeApi';
import { getAccessToken as getAdobeAccessToken } from 'actions/adobeClient';
import Error, { ErrorInput } from 'support/Error';
import getAppStorage from 'common/support/appStorage';
import device from 'common/support/device';
import { Status } from 'models/shortCode';
import { HttpClient } from 'support/api/api-types';
import { VideoCtvRedux } from 'store-types';
import { Provider, AppDispatch } from 'app-types';

export const PARAMOUNT_PLUS_REFRESH_TOKEN_KEY = 'paramountPlusRefreshToken';

export const FETCH_SHORT_CODE = 'FETCH_SHORT_CODE' as const;
export const CLEAR_SHORT_CODE = 'CLEAR_SHORT_CODE' as const;
export const FETCH_SHORT_CODE_STATUS = 'FETCH_SHORT_CODE_STATUS' as const;

export const FETCH_PARAMOUNT_PLUS_TOKEN = 'FETCH_PARAMOUNT_PLUS_TOKEN' as const;
export const REVOKE_PARAMOUNT_PLUS_TOKEN = 'REVOKE_PARAMOUNT_PLUS_TOKEN' as const;
export const FETCH_PARAMOUNT_PLUS_USER_INFO = 'FETCH_PARAMOUNT_PLUS_USER_INFO' as const;
export const CLEAR_PARAMOUNT_PLUS_USER = 'CLEAR_PARAMOUNT_PLUS_USER' as const;

export const SET_TOKEN_EXPIRY = 'SET_TOKEN_EXPIRY' as const;
export const CLEAR_TOKEN_EXPIRY = 'CLEAR_TOKEN_EXPIRY' as const;

export const FETCH_ADOBE_AUTHN = 'FETCH_ADOBE_AUTHN' as const;
export const FETCH_ADOBE_AUTHZ = 'FETCH_ADOBE_AUTHZ' as const;
export const FETCH_MEDIA_TOKEN = 'FETCH_MEDIA_TOKEN' as const;
export const LOGOUT_ADOBE = 'LOGOUT_ADOBE' as const;
export const CLEAR_ADOBE_AUTH = 'CLEAR_ADOBE_AUTH' as const;

export const fetchShortCode = (
  authTypes: Provider[],
  currentSubscriptions: Provider[],
) => async (dispatch: AppDispatch<VideoCtvRedux>) => {
  const deviceId = await device.getDeviceId();
  return dispatch({
    type: FETCH_SHORT_CODE,
    promise: (httpClient: HttpClient) =>
      fetchShortCodeApi(httpClient, deviceId, authTypes, currentSubscriptions),
  });
};

export const clearShortCode = () => ({
  type: CLEAR_SHORT_CODE,
});

interface ShortCodeStatusResponse {
  subscriptionStatus: {
    adobe: string;
    paramountPlus: string;
  };
  refreshToken: string;
}

export const fetchShortCodeStatus = (code: string) => async (dispatch: AppDispatch<VideoCtvRedux>) => {
  const response = await dispatch<ShortCodeStatusResponse>({
    type: FETCH_SHORT_CODE_STATUS,
    promise: (httpClient: HttpClient) => fetchShortCodeStatusApi(httpClient, code),
  });

  if (response) {
    const { refreshToken } = response;

    // Initiate paramountPlus success flow
    if (refreshToken) {
      await getAppStorage().setItem(PARAMOUNT_PLUS_REFRESH_TOKEN_KEY, refreshToken);
      await dispatch(initializeParamountPlusUser(refreshToken));
    }

    if (response.subscriptionStatus?.adobe === Status.SUCCESS) {
      await dispatch(fetchAdobeAuthN());
    }
  }

  return response;
};

export const authStartup = () => (dispatch: AppDispatch<VideoCtvRedux>) => {
  const paramountPlusAuth = dispatch(initializeParamountPlusUser());
  const adobeAuth = dispatch(fetchAdobeAuthN());
  return Promise.all([paramountPlusAuth, adobeAuth]);
};

export const initializeParamountPlusUser = (refreshToken: string | null = null) => async (
  dispatch: AppDispatch<VideoCtvRedux>,
  getState: () => VideoCtvRedux,
) => {
  const token =
    refreshToken || (await getAppStorage().getItem(PARAMOUNT_PLUS_REFRESH_TOKEN_KEY));

  if (token) {
    const { clientId, clientSecret } =
      getState().kitchenSink.data?.providers?.paramountplus || {};
    const response = await dispatch<{ accessToken?: string; expiresIn?: number }>({
      type: FETCH_PARAMOUNT_PLUS_TOKEN,
      promise: (httpClient: HttpClient) =>
        fetchParamountPlusTokenApi(httpClient, token, clientId, clientSecret),
    });

    if (response.accessToken) {
      const { accessToken, expiresIn } = response;

      // Set delay before re-initializing PP user
      const oneDay = 86400000;
      dispatch({
        type: SET_TOKEN_EXPIRY,
        payload: {
          expiresIn: expiresIn && expiresIn < oneDay ? expiresIn : oneDay,
        },
      });

      await dispatch<unknown>({
        type: FETCH_PARAMOUNT_PLUS_USER_INFO,
        meta: {
          expiresIn,
        },
        promise: (httpClient: HttpClient) =>
          fetchParamountPlusUserInfoApi(httpClient, accessToken),
      });
    } else {
      const error = getState().auth?.paramountPlus?.user?.error;
      if (error && typeof error === 'object' && 'message' in error) {
        const errorObj = new Error(error as ErrorInput);
        // If the refresh token is bad, we should wipe it and log
        // the user out
        if (!errorObj.isConnectionError()) {
          dispatch(logoutParamountPlus());
        }
      }
    }
  }
};

export const fetchAdobeAuthN = () => async (dispatch: AppDispatch<VideoCtvRedux>, getState: () => VideoCtvRedux) => {
  const accessToken = await dispatch(getAdobeAccessToken());

  if (accessToken) {
    const host = getState().kitchenSink.data?.providers?.adobe?.authUrl;
    const deviceId = await device.getDeviceId();
    await dispatch<unknown>({
      type: FETCH_ADOBE_AUTHN,
      promise: (httpClient: HttpClient) =>
        fetchAuthNApi(httpClient, host, accessToken, deviceId),
    });
  }
};

export const fetchAdobeAuthZ = (resource: string) => async (dispatch: AppDispatch<VideoCtvRedux>, getState: () => VideoCtvRedux) => {
  const accessToken = await dispatch(getAdobeAccessToken());

  if (accessToken) {
    const host = getState().kitchenSink.data?.providers?.adobe?.authUrl;
    const deviceId = await device.getDeviceId();

    const authZ = await dispatch<unknown>({
      type: FETCH_ADOBE_AUTHZ,
      promise: (httpClient: HttpClient) =>
        fetchAuthZApi(httpClient, host, accessToken, deviceId, resource),
    });

    if (authZ) {
      const mediaToken = await dispatch<unknown>({
        type: FETCH_MEDIA_TOKEN,
        promise: (httpClient: HttpClient) =>
          fetchMediaTokenApi(httpClient, host, accessToken, deviceId, resource),
      });

      return mediaToken;
    }
  }
  return null;
};

export const logoutParamountPlus = () => async (dispatch: AppDispatch<VideoCtvRedux>, getState: () => VideoCtvRedux) => {
  const refreshToken = await getAppStorage().getItem(
    PARAMOUNT_PLUS_REFRESH_TOKEN_KEY,
  );

  if (refreshToken) {
    await getAppStorage().removeItem(PARAMOUNT_PLUS_REFRESH_TOKEN_KEY);
    const { clientId, clientSecret } =
      getState().kitchenSink.data?.providers?.paramountplus || {};
    dispatch<unknown>({
      type: REVOKE_PARAMOUNT_PLUS_TOKEN,
      promise: (httpClient: HttpClient) =>
        revokeParamountPlusTokenApi(
          httpClient,
          refreshToken,
          clientId,
          clientSecret,
        ),
    });
    dispatch({
      type: CLEAR_PARAMOUNT_PLUS_USER,
    });
    dispatch({
      type: CLEAR_TOKEN_EXPIRY,
    });
  }
};

export const logoutAdobe = () => async (dispatch: AppDispatch<VideoCtvRedux>, getState: () => VideoCtvRedux) => {
  const accessToken = await dispatch(getAdobeAccessToken());
  if (accessToken) {
    const host = getState().kitchenSink.data?.providers?.adobe?.authUrl;
    const deviceId = await device.getDeviceId();
    const response = await dispatch<unknown>({
      type: LOGOUT_ADOBE,
      promise: (httpClient: HttpClient) => logoutApi(httpClient, host, accessToken, deviceId),
    });
    if (response) dispatch({ type: CLEAR_ADOBE_AUTH });
  }
};
